"use client";

import { createFileRoute, Link } from '@tanstack/react-router'
import { useQuery, useMutation } from '@tanstack/react-query'
import { useState, useEffect } from 'react'
import { z } from 'zod'
import AdminToolbar from '@/src/components/layout/AdminToolbar'
import { Button } from '@/src/components/ui/button'
import { Input } from '@/src/components/ui/input'
import { DataTable, type ColumnConfig } from '@/src/components/manage/tables/DataTable'
import { ArrowCounterClockwiseIcon, ArrowsClockwiseIcon } from '@phosphor-icons/react'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/src/components/ui/select'
import { Pagination } from '@/src/components/manage/Pagination'
import { listFiles, deleteFile } from '@/src/api/file'
import { ShareLinkCell } from '@/src/components/manage/tables/CopyableCell'
import { useTranslation } from '@/src/i18n/client'
import { ReanalyzeAction } from '@/src/components/manage/actions/ReanalyzeAction'
import { DeleteAction } from '@/src/components/manage/actions/DeleteAction'
import { IsometricIcon } from '@/src/components/manage/tables/IsometricIcon'
import { Badge } from '@/src/components/ui/badge';
import { useAuth } from '@/src/components/AuthContextProvider';
import { DateTimeText } from '@/src/components/ui/date-time'
import { FilePreviewPopover } from '@/src/components/file/FilePreviewPopover'
import { FilterAction, ViewAction } from '@/src/components/manage/actions';
import { toast } from 'sonner'
import { ConfirmModal } from '@/src/components/ui/confirm-modal'
import { reanalyzeFile } from '@/src/api/ai'
import { listPrompts } from '@/src/api/prompt'
import { listModels } from '@/src/api/model'

const PAGE_SIZE = 20

const searchSchema = z.object({
  page: z.coerce.number().int().min(1).optional(),
})

export const Route = createFileRoute('/_manage/manage/admin/files/')({
  component: AdminFilesPage,
  validateSearch: searchSchema,
})

function AdminFilesPage() {
  const { t } = useTranslation()
  const searchParams = Route.useSearch()
  const navigate = Route.useNavigate()
  const [search, setSearch] = useState('')
  const [userId, setUserId] = useState('')
  const { user, isAdmin } = useAuth()
  const [projectId, setProjectId] = useState('')
  const page = (searchParams.page ?? 1) - 1
  const setPage = (nextPage: number) => {
    void navigate({
      search: (prev) => ({
        ...prev,
        page: nextPage > 0 ? nextPage + 1 : undefined,
      }),
    })
  }
  const [searchType, setSearchType] = useState<'user' | 'project' | 'search'>('search')
  const [sort, setSort] = useState<{ key: string; direction: 'asc' | 'desc' }>({
    key: 'createdAt',
    direction: 'desc',
  })
  const [hoveredFile, setHoveredFile] = useState<{ fileId: string; thumbnailKey: string | null; position: { x: number; y: number } } | null>(null)
  const [selectedItems, setSelectedItems] = useState<string[]>([])
  const [showBulkReanalyzeConfirm, setShowBulkReanalyzeConfirm] = useState(false)
  const [defaultPromptId, setDefaultPromptId] = useState<string>('')
  const [defaultModelId, setDefaultModelId] = useState<string>('')

  // Fetch prompts and models for bulk reanalyze
  const { data: prompts } = useQuery({
    queryKey: ['prompts'],
    queryFn: () => listPrompts({ data: {} }),
  })

  const { data: models } = useQuery({
    queryKey: ['models'],
    queryFn: () => listModels({ data: {} }),
  })

  // Set default prompt and model when loaded
  useEffect(() => {
    if (prompts && prompts.length > 0 && !defaultPromptId) {
      setDefaultPromptId(prompts[0].id)
    }
  }, [prompts, defaultPromptId])

  useEffect(() => {
    if (models && models.length > 0 && !defaultModelId) {
      setDefaultModelId(models[0].id)
    }
  }, [models, defaultModelId])

  const deleteMutation = useMutation({
    mutationFn: (shareId: string) => deleteFile({ data: { shareId } }),
    onSuccess: () => {
      refetch()
    },
  })

  const bulkDeleteMutation = useMutation({
    mutationFn: async (shareIds: string[]) => {
      // Delete files sequentially
      const results = await Promise.allSettled(
        shareIds.map(shareId => deleteFile({ data: { shareId } }))
      )
      const failed = results.filter(r => r.status === 'rejected').length
      if (failed > 0) {
        throw new Error(`Failed to delete ${failed} of ${shareIds.length} items`)
      }
      return results
    },
    onSuccess: (_, shareIds) => {
      toast.success(t('table.bulkDeleteSuccess', { count: shareIds.length }))
      setSelectedItems([])
      refetch()
    },
    onError: () => {
      toast.error(t('table.bulkDeleteFailed'))
    },
  })

  const bulkReanalyzeMutation = useMutation({
    mutationFn: async (params: { shareIds: string[]; promptId: string; modelId: string }) => {
      const { shareIds, promptId, modelId } = params
      // Reanalyze files sequentially
      const results = await Promise.allSettled(
        shareIds.map(shareId => 
          reanalyzeFile({ 
            data: { 
              fileShareId: shareId, 
              promptId, 
              aiModelId: modelId 
            } 
          })
        )
      )
      const failed = results.filter(r => r.status === 'rejected').length
      if (failed > 0) {
        throw new Error(`Failed to reanalyze ${failed} of ${shareIds.length} files`)
      }
      return results
    },
    onSuccess: (_, params) => {
      toast.success(`Successfully queued ${params.shareIds.length} files for reanalysis`)
      setSelectedItems([])
      setShowBulkReanalyzeConfirm(false)
      refetch()
    },
    onError: (error: any) => {
      toast.error(error?.message || 'Failed to reanalyze files')
    },
  })

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['file', 'listAll', { search, userId, projectId, page, sort }],
    queryFn: () => listFiles({
      data: {
        all: true,
        search: search || undefined,
        userId: userId || undefined,
        projectId: projectId || undefined,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
        orderBy: sort.key,
        order: sort.direction,
      },
    }),
  })

  const files = data?.files ?? []
  const total = data?.total ?? 0
  const pageCount = Math.ceil(total / PAGE_SIZE)

  return (
    <div className="bg-background text-foreground">
      <AdminToolbar selectedCount={selectedItems.length}>
        <AdminToolbar.BulkActions>
          <Button
            variant="outline"
            size="lg"
            onClick={() => setShowBulkReanalyzeConfirm(true)}
            disabled={bulkReanalyzeMutation.isPending || !defaultPromptId || !defaultModelId}
          >
            <ArrowsClockwiseIcon className="w-4 h-4 mr-2" />
            Reanalyze
          </Button>
          <DeleteAction
            itemName={`${selectedItems.length} file(s)`}
            itemType="files"
            buttonLabel="Delete"
            showText={true}
            variant="destructive"
            size="lg"
            onDelete={async () => {
              const selectedFiles = files.filter(f => selectedItems.includes(f.id))
              const shareIds = selectedFiles.map(f => f.shareId).filter(Boolean)
              await bulkDeleteMutation.mutateAsync(shareIds)
            }}
            isPending={bulkDeleteMutation.isPending}
          />
        </AdminToolbar.BulkActions>
        <AdminToolbar.Right>
          <FilterAction>
            <div className="flex flex-col gap-3">
              <Select
                value={userId ? 'user' : projectId ? 'project' : (search ? 'search' : 'search')}
                onValueChange={val => {
                  if (val === 'user') {
                    setSearchType('user')
                    setUserId('')
                    setProjectId('')
                    setSearch('')
                  } else if (val === 'project') {
                    setSearchType('project')
                    setUserId('')
                    setProjectId('')
                    setSearch('')
                  } else {
                    setSearchType('search')
                    setUserId('')
                    setProjectId('')
                    setSearch('')
                  }
                  setPage(0)
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Search or Filter" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="search">Search</SelectItem>
                  <SelectItem value="user">Filter User</SelectItem>
                  <SelectItem value="project">Filter Project</SelectItem>
                </SelectContent>
              </Select>
              <Input
                placeholder={searchType === 'search' ? 'Search files' : searchType === 'user' ? 'User ID' : 'Project ID'}
                value={userId || projectId || search}
                onChange={e => {
                  if (searchType === 'user') {
                    setUserId(e.target.value)
                  } else if (searchType === 'project') {
                    setProjectId(e.target.value)
                  } else {
                    setSearch(e.target.value)
                  }
                  setPage(0)
                }}
              />
              <div className="flex justify-end">
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => {
                    setSearch('')
                    setUserId('')
                    setProjectId('')
                    setPage(0)
                  }}
                  aria-label="Reset"
                >
                  <ArrowCounterClockwiseIcon className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </FilterAction>
          <Pagination
            page={page}
            totalPages={pageCount}
            onPageChange={setPage}
            compact
            className="ml-auto"
          />
        </AdminToolbar.Right>
      </AdminToolbar>
      <div className="mt-4">
        <DataTable
          data={files}
          isLoading={isLoading}
          error={error instanceof Error ? error.message : undefined}
          emptyMessage={t('table.noFiles')}
          columns={[
            {
              key: 'shareId',
              label: t('table.shareId'),
              width: 'w-24',
              sortable: true,
              render: (file: any) => {

                const canRotate = isAdmin || (!!file.user?.id && file.user.id === user?.id)
                return <ShareLinkCell shareId={file.shareId} canRotate={canRotate} />
              }
            },
            {
              key: 'isometric',
              label: '',
              width: 'w-10',
              render: (file: any) => {
                if (!file.id) return null;
                return <IsometricIcon fileId={file.id} />;
              },
            },
            {
              key: 'name',
              label: t('table.name'),
              width: 'w-[20%]',
              sortable: true,
              primary: true,
              render: (file: any) => (
                <div
                  className="relative inline-flex items-center group"
                  onMouseEnter={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect()
                    setHoveredFile({
                      fileId: file.id,
                      thumbnailKey: file.thumbnailLargeUrl,
                      position: { x: rect.right + 8, y: rect.top }
                    })
                  }}
                  onMouseLeave={() => setHoveredFile(null)}
                >
                  <div className="max-w-45 truncate" title={file.name}>
                    {file.name}
                  </div>
                </div>
              )
            },
            {
              key: 'projectId',
              label: t('table.project'),
              width: 'w-[10%]',
              sortable: false
            },
            {
              key: 'drawings',
              label: t('table.drawings'),
              width: 'w-[10%]',
              align: 'center',
              sortable: false,
              render: (file: any) => {
                const count = Array.isArray(file.drawingIds) ? file.drawingIds.length : 0
                if (count === 0) return <span className="text-muted-foreground">0</span>
                return (
                  <Link
                    to="/manage/admin/drawings"
                    search={{ fileId: file.id }}
                    className="text-primary hover:underline font-medium"
                  >
                    <Badge variant="outline" className="px-2">
                      {count}
                    </Badge>
                  </Link>
                )
              }
            },
            {
              key: 'userCount',
              label: 'Users',
              width: 'w-[10%]',
              align: 'center',
              render: (file: any) => <Badge variant="outline" className="px-2">{file.userCount ?? 0}</Badge>
            },
            {
              key: 'createdAt',
              label: t('table.date'),
              width: 'w-[12%]',
              sortable: true,
              sortDefault: 'desc',
              render: (file: any) => <DateTimeText>{file.createdAt}</DateTimeText>
            }
          ]}
          sort={sort}
          onSortChange={(next) => {
            setSort(next ?? { key: 'createdAt', direction: 'desc' })
            setPage(0)
          }}
          selectable={true}
          selectedItems={selectedItems}
          onSelectionChange={setSelectedItems}
          totalItems={total}
          actions={(file: any) => (
            <>
              <ViewAction
                to={`/manage/admin/files/${file.shareId}`}
              />
              <ReanalyzeAction
                file={{
                  id: file.id,
                  shareId: file.shareId,
                  name: file.name,
                  drawingIds: file.drawingIds,
                }}
              />
              <DeleteAction
                itemName={file.name}
                itemType="file"
                onDelete={() => deleteMutation.mutateAsync(file.shareId).then(() => { })}
                isPending={deleteMutation.isPending}
              />
            </>
          )}
        />
      </div>
      <FilePreviewPopover
        fileId={hoveredFile?.fileId ?? ''}
        thumbnailKey={hoveredFile?.thumbnailKey ?? null}
        position={hoveredFile?.position ?? { x: 0, y: 0 }}
        isVisible={!!hoveredFile}
        onClose={() => setHoveredFile(null)}
      />
      <ConfirmModal
        open={showBulkReanalyzeConfirm}
        onOpenChange={setShowBulkReanalyzeConfirm}
        title={`Reanalyze ${selectedItems.length} file${selectedItems.length > 1 ? 's' : ''}?`}
        description={
          selectedItems.length > 10 
            ? `⚠️ Warning: You are about to reanalyze ${selectedItems.length} files. This will create new analysis jobs for each file using the default prompt and model settings. This operation may take considerable time.`
            : `This will create new analysis jobs for ${selectedItems.length} file${selectedItems.length > 1 ? 's' : ''} using the default prompt and model settings.`
        }
        confirmLabel="Reanalyze"
        cancelLabel={t('action.cancel')}
        loadingLabel="Processing..."
        variant="default"
        onConfirm={() => {
          if (!defaultPromptId || !defaultModelId) {
            toast.error('No default prompt or model configured')
            return
          }
          // Map selected file IDs to shareIds
          const selectedFiles = files.filter(f => selectedItems.includes(f.id))
          const shareIds = selectedFiles.map(f => f.shareId).filter(Boolean)
          bulkReanalyzeMutation.mutate({ shareIds, promptId: defaultPromptId, modelId: defaultModelId })
        }}
        isLoading={bulkReanalyzeMutation.isPending}
      />
    </div>
  )
}